import { useMemo } from 'react';
import { BoardTheme } from '@/hooks/useCosmetics';
import { getPieceImages, PieceStyle } from '@/lib/pieces';

interface MiniChessBoardProps {
  boardTheme?: BoardTheme | null;
  pieceStyle?: PieceStyle;
  size?: 'sm' | 'md' | 'lg';
  animated?: boolean;
}

// Sample pieces for preview (showing a few key pieces)
const previewPieces: Record<string, string> = {
  '0-0': 'r', // black rook
  '0-3': 'q', // black queen
  '1-1': 'p', // black pawn
  '1-2': 'n', // black knight
  '2-1': 'P', // white pawn
  '2-2': 'N', // white knight
  '3-0': 'R', // white rook
  '3-3': 'Q', // white queen
};

export function MiniChessBoard({ 
  boardTheme, 
  pieceStyle = 'modern',
  size = 'md',
  animated = true 
}: MiniChessBoardProps) {
  const pieceImages = useMemo(() => getPieceImages(pieceStyle), [pieceStyle]);
  
  const sizeClasses = {
    sm: 'w-20 h-20',
    md: 'w-32 h-32',
    lg: 'w-48 h-48'
  };

  const defaultLightSquare = 'hsl(45, 26%, 87%)';
  const defaultDarkSquare = 'hsl(159, 55%, 35%)';

  return (
    <div 
      className={`${sizeClasses[size]} rounded-lg overflow-hidden shadow-lg border border-border/50 ${
        animated ? 'transition-all duration-500 ease-out' : ''
      } ${boardTheme?.glow ? 'animate-pulse' : ''}`}
      style={{
        boxShadow: boardTheme?.glow 
          ? `0 0 20px ${boardTheme.glowColor || 'hsl(280, 70%, 50%)'}` 
          : undefined
      }}
    >
      <div className="grid grid-cols-4 w-full h-full">
        {Array.from({ length: 16 }).map((_, index) => {
          const row = Math.floor(index / 4);
          const col = index % 4;
          const isLight = (row + col) % 2 === 0;
          const pieceKey = `${row}-${col}`;
          const piece = previewPieces[pieceKey];

          return (
            <div
              key={index}
              className={`relative flex items-center justify-center ${
                animated ? 'transition-all duration-500 ease-out' : ''
              }`}
              style={{
                backgroundColor: isLight 
                  ? (boardTheme?.lightSquare || defaultLightSquare)
                  : (boardTheme?.darkSquare || defaultDarkSquare),
              }}
            >
              {piece && pieceImages[piece] && (
                <img 
                  src={pieceImages[piece]} 
                  alt={piece}
                  className={`w-[85%] h-[85%] object-contain ${
                    animated ? 'transition-transform duration-300' : ''
                  }`}
                  style={{
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                  }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
